import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BookmarkBorder from '@mui/icons-material/BookmarkBorder';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import DefaultInterfacesHandler from '../handlers/DefaultInterfacesHandler';
// import { motion } from 'framer-motion';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import ListSubheader from '@mui/material/ListSubheader';
import Button from '@mui/material/Button';
import useElDimensions from '../../hooks/useElDimensions';
import useBookableCurator from '../../hooks/useBookableCurator';
import { useCallback } from 'react';

import usePropsBeforeContext from '../../hooks/usePropsBeforeContext';
import { CalendarViewContext } from '../Calendar';
import { isObject, isString } from 'lodash';
import useFirstBookbale from '../../hooks/useFirstBookbale';
import ProductSelectorButton from '../base/ProductSelectorButton';
import { CrossFade } from '../base/CrossFade';
import { LOADING_GRACE_MS, LOADING_MIN_DISPLAY_MS } from '../../hooks/useSmartLoading';
import NoBookables from '../base/NoBookables';

/**
 * Width threshold for responsive layout switching.
 * Below this width, the component uses stacked layout (time over product name).
 * Above this width, the component uses horizontal layout (time left, product centered).
 */
export const SMALL_CONTAINER_THRESHOLD_PX = 360;

function useMaxTimeWidthPx(times, getTimeString, fontTarget) {
  const [px, setPx] = React.useState('0px');

  React.useLayoutEffect(() => {
    if (!times?.length) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    // Use the same computed font as your Typography
    const font = getComputedStyle(fontTarget ?? document.body).font;
    ctx.font = font;

    const max = times
      .map(getTimeString)
      .reduce((m, s) => Math.max(m, Math.ceil(ctx.measureText(s).width)), 0);

    setPx(`${max}px`);
  }, [times, getTimeString, fontTarget]);

  return px;
}

function Blocker({ viewport }) {
  const { t } = useTranslation();

  return (
    <ListItem
      // component={motion.li}
      viewport={{ root: viewport }}
      // initial={{ opacity: 0 }}
      // whileInView={{ opacity: 1 }}
      // exit={{ opacity: 0 }}
      // transition={{ duration: 0.2, delay: 0.05 }}
      sx={{
        pointerEvents: 'none',
        marginBottom: '8px',
      }}
    >
      <ListItemText
        sx={{
          width: '100%',
          textAlign: 'center',
          py: { xs: 0, sm: 0, md: 0 },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.disabled',
          }}
        >
          {`${t('appoinment_at_capacity')}`}
        </Typography>
      </ListItemText>
    </ListItem>
  );
}

const Bookable = React.memo(
  React.forwardRef(
    (
      { timeslot, productName, openBookingPage, viewport, isSmallContainer },
      ref,
    ) => {
      const timeString = timeslot?.start?.toLocaleString(DateTime.TIME_SIMPLE);

      return (
        <ListItem
          ref={ref}
          disablePadding
          // component={motion.li}
          viewport={{ root: viewport }}
          // initial={{ opacity: 0 }}
          // whileInView={{ opacity: 1 }}
          // exit={{ opacity: 0 }}
          // transition={{ duration: 0.2, delay: 0.05 }}
          sx={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: (theme) => theme.palette.primary.main,
            // margin: '15px',
            marginBottom: '8px',
          }}
        >
          <ListItemButton
            sx={{
              py: isSmallContainer ? 1 : 2,
            }}
            onClick={() => {
              openBookingPage(timeslot);
            }}
          >
            {isSmallContainer ? (
              // Small container: Stacked layout using ListItemText primary/secondary
              <ListItemText
                primary={timeString}
                secondary={productName || ' '}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { color: 'primary.main' },
                }}
                secondaryTypographyProps={{
                  variant: 'body2',
                  noWrap: true,
                }}
              />
            ) : (
              // Large container: Horizontal layout with centered product name
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                {/* Left-aligned time (fixed width) */}
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    flex: '0 0 var(--max-time-width)',
                    color: 'primary.main',
                  }}
                >
                  {timeString}
                </Typography>
                {/* Product name centered in remaining space.
                    minWidth:0 overrides the flexbox default (min-width:auto)
                    which would prevent shrinking below the text content width. */}
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    textAlign: 'center',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mx: 1,
                  }}
                >
                  {productName || ' '}
                </Typography>
                {/* Invisible spacer matching time width for visual centering */}
                <Box sx={{ flex: '0 0 var(--max-time-width)' }} />
              </Box>
            )}
          </ListItemButton>
        </ListItem>
      );
    },
  ),
);
Bookable.displayName = 'Bookable';

function CancelableBookable({
  timeslot,
  openBookingPage,
  viewport,
  isSmallContainer,
}) {
  const { t } = useTranslation();
  const timeString = timeslot?.start?.toLocaleString(DateTime.TIME_SIMPLE);
  const labelText = t('cancellation.cancellable_appointment_highlight');

  return (
    <ListItem
      disablePadding
      // component={motion.li}
      viewport={{ root: viewport }}
      // initial={{ opacity: 0 }}
      // whileInView={{ opacity: 1 }}
      // exit={{ opacity: 0 }}
      // transition={{ duration: 0.2, delay: 0.05 }}
      sx={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'secondary.main',
        marginBottom: '8px',
      }}
    >
      <ListItemButton
        sx={{
          py: isSmallContainer ? 1 : 2,
        }}
        onClick={() => {
          openBookingPage(timeslot);
        }}
      >
        {isSmallContainer ? (
          // Small container: Stacked layout
          <ListItemText
            primary={timeString}
            secondary={labelText}
            primaryTypographyProps={{
              variant: 'body2',
              sx: { color: 'primary.main' },
            }}
            secondaryTypographyProps={{
              variant: 'body2',
              noWrap: true,
            }}
          />
        ) : (
          // Large container: Horizontal layout with centered label
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                flex: '0 0 var(--max-time-width)',
                color: 'primary.main',
              }}
            >
              {timeString}
            </Typography>
            {/* minWidth:0 overrides flexbox default (min-width:auto)
                which would prevent shrinking below the text content width. */}
            <Typography
              variant="body2"
              noWrap
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                textAlign: 'center',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mx: 1,
              }}
            >
              {labelText}
            </Typography>
            {/* Invisible spacer matching time width for visual centering */}
            <Box sx={{ flex: '0 0 var(--max-time-width)' }} />
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
}

function SubHeader({ date, isSmallContainer }) {
  const dateFormat = isSmallContainer ? DateTime.DATE_MED : DateTime.DATE_HUGE;

  return (
    <ListSubheader
      sx={{
        mb: 1,
        pl: 0,
        // Explicitly ensure sticky behavior works (MUI defaults may not apply in all cases)
        zIndex: 2, // Higher than Bookable's internal zIndex: 1
      }}
    >
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '48px',
        }}
      >
        <Grid item xs={12}>
          <Typography variant="body1">
            {DateTime.fromISO(date).toLocaleString(dateFormat)}
          </Typography>
        </Grid>
      </Grid>
    </ListSubheader>
  );
}

/**
 * Displays bookables in a vertical list.
 * @param {} param0
 * @returns
 */
export default function PureListView(props) {
  const { t } = useTranslation();
  const {
    isFetching,
    bookables,
    products,
    product,
    resource,
    publicDataMap,
    openResourcePage,
    openProductPage,
    openBookingPage,
    height,
    suppressDefaultInterfaces = false,
    suppressDateHeaders = false,
    suppressProductSelector = false,
    forceSmallContainer,
  } = usePropsBeforeContext(props, CalendarViewContext);

  const listRef = React.useRef(null);
  const fontRef = React.useRef(null);

  const [autoScrollIndex, setAutoScrollIndex] = React.useState();
  const [oldProduct, setOldProduct] = React.useState();
  const [prdBtnRef, prdBtnDimensions] = useElDimensions(undefined, 'outer');
  const [resBtnRef, resBtnDimensions] = useElDimensions(undefined, 'outer');
  const [containerRef, containerDimensions] = useElDimensions(
    undefined,
    'outer',
  );

  // Derive responsive state from own container width.
  // Parent components (e.g. EyeCandy) can override this via forceSmallContainer
  // when they have better knowledge of the actual available width (e.g. when
  // PureListView is inside an animating container whose measured width is unreliable).
  const isSmallContainer =
    forceSmallContainer != null
      ? forceSmallContainer
      : containerDimensions?.width
        ? containerDimensions.width < SMALL_CONTAINER_THRESHOLD_PX
        : false;

  const parsedBookables = useBookableCurator({
    bookables,
  });

  const { index, unifiedBookables } = useFirstBookbale(parsedBookables);

  const maxTimeWidth = useMaxTimeWidthPx(
    unifiedBookables?.filter((slot) => slot.start).map((slot) => slot.start) ||
      [],
    (time) => time?.toLocaleString(DateTime.TIME_SIMPLE),
    fontRef.current,
  );

  const onRefChange = useCallback(
    (node) => {
      if (node && autoScrollIndex) {
        node.scrollIntoView({
          behavior: 'smooth', // Optional: define the scrolling behavior
          block: 'center', // Optional: define vertical alignment
        });
      }
    },
    [autoScrollIndex],
  );

  useMemo(() => {
    if (index && oldProduct !== product) {
      setAutoScrollIndex(index);
      setOldProduct(product);
    }
  }, [index, oldProduct, product]);

  const shouldDisplayResourceSelect = useMemo(() => {
    return (
      openResourcePage &&
      isSmallContainer &&
      publicDataMap &&
      Object.keys(publicDataMap).length > 1
    );
  }, [isSmallContainer, openResourcePage, publicDataMap]);

  const btnHeight =
    (prdBtnDimensions?.height || 0) + (resBtnDimensions?.height || 0);

  return (
    <>
      <Typography
        ref={fontRef}
        variant="body2"
        sx={{
          position: 'absolute',
          left: -10000,
          opacity: 0,
        }}
      >
        Font Test
      </Typography>
      <CrossFade
        showSkeleton={isFetching || (!product && products?.length > 0)}
        graceMs={LOADING_GRACE_MS}
        minDisplayMs={LOADING_MIN_DISPLAY_MS}
        skeleton={
          <LoadingSkeleton
            height={height}
            suppressDateHeaders={suppressDateHeaders}
            suppressProductSelector={suppressProductSelector}
          />
        }
        height={height}
      >
        <Grid
          ref={containerRef}
          container
          sx={{
            m: 0,
          }}
        >
          {shouldDisplayResourceSelect && (
            <Grid item xs={12}>
              <Button
                ref={resBtnRef}
                variant="outlined"
                size="small"
                fullWidth
                onClick={openResourcePage}
                startIcon={<BookmarkBorder fontSize="small" />}
                endIcon={<ExpandMoreIcon fontSize="10px" />}
              >
                {resource?.name || t('resource_selection_headline')}
              </Button>
            </Grid>
          )}
          {openProductPage && unifiedBookables?.length && (
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: isSmallContainer ? 'stretch' : 'flex-end',
                pb: 1,
              }}
            >
              <ProductSelectorButton
                ref={prdBtnRef}
                openProductPage={openProductPage}
                product={product}
                products={products}
                buttonProps={{
                  fullWidth: isSmallContainer,
                }}
              >
                {product?.name || t('product_selection_headline')}
              </ProductSelectorButton>
            </Grid>
          )}
          <Grid item xs={12}>
            <List
              ref={listRef}
              sx={{
                '--max-time-width': maxTimeWidth,
                '& ul': { padding: 0 },
                position: 'relative',
                height:
                  height - (btnHeight + 24) /* btnHeight + spacingTop */ ||
                  '500px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              subheader={<li />}
            >
              {!unifiedBookables?.length && (
                <NoBookables
                  openProductPage={openProductPage}
                  product={product}
                  products={products}
                  prdBtnRef={prdBtnRef}
                />
              )}
              {unifiedBookables?.length > 0 &&
                unifiedBookables.map((data, i) => {
                  if (isString(data) && !suppressDateHeaders) {
                    return (
                      <SubHeader
                        key={data}
                        date={data}
                        isSmallContainer={isSmallContainer}
                      />
                    );
                  }
                  if (isObject(data)) {
                    const timeslot = data;
                    if (timeslot.renderAsCancelable) {
                      return (
                        <CancelableBookable
                          key={`${timeslot.start.toISO()}-${timeslot.uuid}`}
                          viewport={listRef}
                          index={i}
                          timeslot={timeslot}
                          openBookingPage={openBookingPage}
                          isSmallContainer={isSmallContainer}
                        />
                      );
                    } else if (timeslot.renderAsBookable) {
                      let productName = product?.name;
                      if (
                        !timeslot.hasSelectedProduct &&
                        timeslot.kind !== 'models.Bookable' // pure tsls have no product nor a custom one anyway
                      ) {
                        productName = timeslot.product_name;
                      }

                      return (
                        <Bookable
                          key={`${timeslot.start.toISO()}-${timeslot.uuid}`}
                          ref={autoScrollIndex === i ? onRefChange : undefined}
                          viewport={listRef}
                          index={i}
                          timeslot={timeslot}
                          productName={productName}
                          openBookingPage={openBookingPage}
                          isSmallContainer={isSmallContainer}
                        />
                      );
                    } else if (timeslot.renderAsBlocker) {
                      return (
                        <Blocker
                          index={i}
                          viewport={listRef}
                          key={`${timeslot.start.toISO()}-${timeslot.uuid}`}
                        />
                      );
                    }
                  }
                })}
            </List>
          </Grid>
        </Grid>
        {!suppressDefaultInterfaces && <DefaultInterfacesHandler />}
      </CrossFade>
    </>
  );
}

const LoadingSkeleton = ({
  height = 500,
  suppressDateHeaders,
  suppressProductSelector,
}) => {
  const [count, setCount] = React.useState();

  useEffect(() => {
    if (!height) {
      setCount(Math.floor(500 / 133));
    } else {
      setCount(Math.floor(height / 133));
    }
  }, [height]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Prod Button */}
      {suppressProductSelector && (
        <Skeleton
          variant="rounded"
          height="36px"
          width="160px"
          sx={{
            position: 'absolute',
            right: '0px',
          }}
        />
      )}
      {/* Date and Day */}
      {!suppressDateHeaders && (
        <Typography
          variant="subtitle1"
          component="div"
          sx={{ mb: 1, pt: '44px' }}
        >
          <Skeleton width="120px" />
        </Typography>
      )}
      {/* Time Slots */}
      {[...Array(3)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mb: '8px',
            height: '56px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: (theme) => theme.palette.divider,
            paddingLeft: '16px',
            borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          }}
        >
          <Skeleton variant="text" height="24px" width="40%" />
        </Box>
      ))}
      {[...Array(count)].map((_, outerIndex) => {
        return (
          <React.Fragment key={outerIndex}>
            {/*   /* Date and Day */}
            {!suppressDateHeaders && (
              <Typography
                variant="subtitle1"
                component="div"
                sx={{ mb: '8px' }}
              >
                <Skeleton width="120px" />
              </Typography>
            )}

            {/* Time Slots */}
            {[...Array(2)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  mb: '8px',
                  height: '56px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: (theme) => theme.palette.divider,
                  paddingLeft: '16px',
                  borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                }}
              >
                <Skeleton variant="text" height="24px" width="40%" />
              </Box>
            ))}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

PureListView.propTypes = {
  bookables: PropTypes.object.isRequired,
  product: PropTypes.object,
  products: PropTypes.array,
  openProductPage: PropTypes.func,
  openBookingPage: PropTypes.func,
  height: PropTypes.number,
};
